"use client";

import { useState, useRef, useEffect, FormEvent } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Compass, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { authFetch } from "../../lib/authFetch";
import { isProPlan } from "../../lib/plans";
import MarkdownMessage from "./MarkdownMessage";
import { API_BASE } from "../../lib/api";

interface Message {
  role: "user" | "assistant";
  content: string;
}

// Voice per DESIGN-LANGUAGE.md: conversational but composed.
// No exclamation points in body copy. Open question invites the user
// to anchor the conversation in something concrete.
const WELCOME: Message = {
  role: "assistant",
  content: "I'm Maren. What are you working on?",
};

// Floating coach button shown on every page except the full chat surface
// at /maren. Was LiraButton; renamed to MarenButton as part of the
// brand-substrate chrome migration. The old /lira route 308-redirects
// to /maren via next.config.ts so existing bookmarks keep working.
export default function MarenButton() {
  const { session } = useAuth();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([WELCOME]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [plan, setPlan] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!session) return;
    authFetch(`${API_BASE}/billing/plan`)
      .then((r) => r.json())
      .then((d) => setPlan(d.plan))
      .catch(() => setPlan("free"));
  }, [session]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  if (!session || pathname === "/maren") return null;

  async function send(e?: FormEvent) {
    e?.preventDefault();
    const text = input.trim();
    if (!text || streaming) return;

    const userMsg: Message = { role: "user", content: text };
    const historyForApi = messages.map((m) => ({ role: m.role, content: m.content }));

    setMessages((prev) => [...prev, userMsg, { role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);

    try {
      const res = await authFetch(`${API_BASE}/coach/chat`, {
        method: "POST",
        body: JSON.stringify({ message: text, history: historyForApi }),
      });

      if (!res.ok || !res.body) throw new Error("Request failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") break;
          try {
            const { text: chunk } = JSON.parse(payload);
            if (chunk) {
              setMessages((prev) => {
                const last = prev[prev.length - 1];
                if (last.role === "assistant") {
                  return [...prev.slice(0, -1), { ...last, content: last.content + chunk }];
                }
                return prev;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((prev) => [
        ...prev.slice(0, -1),
        { role: "assistant", content: "Something went wrong. Try again." },
      ]);
    } finally {
      setStreaming(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") send();
  }

  return (
    <>
      {/* Floating button -- Compass icon echoes the Maren mark. */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Chat with Maren"
        className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-maren-green hover:bg-maren-green/90 shadow-lg flex items-center justify-center text-maren-stone transition-all hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-maren-gold/50 focus:ring-offset-2"
        aria-label={open ? "Close chat" : "Open chat with Maren"}
      >
        {open ? <X size={20} strokeWidth={1.5} /> : <Compass size={22} strokeWidth={1.5} />}
      </button>

      {/* Mini chat panel */}
      {open && (
        <div
          className="fixed bottom-20 right-6 z-50 w-80 rounded-2xl shadow-2xl border border-maren-sage/40 dark:border-maren-sage/20 bg-maren-stone dark:bg-maren-night flex flex-col overflow-hidden"
          style={{ height: "420px" }}
        >
          {/* Header */}
          <div className="flex items-center gap-2.5 px-4 py-3 bg-maren-charcoal shrink-0 border-b border-maren-charcoal-elevated">
            <Compass size={18} strokeWidth={1.5} className="text-maren-gold" aria-hidden="true" />
            <p className="text-sm font-semibold text-maren-stone tracking-wide">Maren</p>
            <Link
              href="/maren"
              onClick={() => setOpen(false)}
              className="ml-auto text-xs text-maren-stone/70 hover:text-maren-gold transition"
            >
              Open full chat →
            </Link>
          </div>

          {/* Messages */}
          {plan === null ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-maren-sage border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !isProPlan(plan) ? (
            <div className="flex-1 flex flex-col items-center justify-center px-4 text-center gap-3">
              <p className="text-sm text-maren-charcoal/80 dark:text-maren-stone/80">
                The Maren coach is a paid feature.
              </p>
              <Link
                href="/pricing"
                onClick={() => setOpen(false)}
                className="rounded-lg bg-maren-green px-4 py-2 text-xs font-semibold text-maren-stone hover:bg-maren-green/90 transition shadow-sm"
              >
                See plans
              </Link>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-maren-stone dark:bg-maren-night">
                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[85%] rounded-xl px-3 py-2 text-xs leading-relaxed ${
                        msg.role === "user"
                          ? "bg-maren-green text-maren-stone rounded-tr-sm"
                          : "bg-white dark:bg-maren-charcoal text-maren-charcoal dark:text-maren-stone rounded-tl-sm border border-maren-sage/40 dark:border-maren-sage/20"
                      }`}
                    >
                      {msg.role === "assistant" && msg.content === "" && streaming ? (
                        <span className="inline-flex gap-1 items-center text-maren-charcoal/40 dark:text-maren-stone/40">
                          <span className="animate-bounce" style={{ animationDelay: "0ms" }}>·</span>
                          <span className="animate-bounce" style={{ animationDelay: "150ms" }}>·</span>
                          <span className="animate-bounce" style={{ animationDelay: "300ms" }}>·</span>
                        </span>
                      ) : msg.role === "assistant" ? (
                        <MarkdownMessage text={msg.content} />
                      ) : (
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <div className="shrink-0 border-t border-maren-sage/40 dark:border-maren-sage/20 px-3 py-2 flex gap-2 bg-maren-stone dark:bg-maren-night">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={streaming}
                  placeholder="Ask Maren…"
                  className="flex-1 rounded-lg border border-maren-sage/40 dark:border-maren-sage/20 bg-white dark:bg-maren-charcoal px-3 py-1.5 text-xs text-maren-charcoal dark:text-maren-stone placeholder:text-maren-charcoal/40 dark:placeholder:text-maren-stone/40 outline-none focus:border-maren-green focus:ring-2 focus:ring-maren-gold/30 transition disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={() => send()}
                  disabled={streaming || !input.trim()}
                  className="rounded-lg bg-maren-green px-3 py-1.5 text-xs font-semibold text-maren-stone hover:bg-maren-green/90 disabled:opacity-40 transition shadow-sm focus:outline-none focus:ring-2 focus:ring-maren-gold/30"
                >
                  {streaming ? "…" : "→"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
